import React, { useEffect, useState, useMemo } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { db } from './firebaseConfig';
import {
  Box, Typography, Avatar, Chip, Card, CardContent,
  CircularProgress, IconButton, Skeleton, Tooltip
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import PetsIcon from '@mui/icons-material/Pets';
import ShareIcon from '@mui/icons-material/Share';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import LanguageIcon from '@mui/icons-material/Language';

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#0284c7' },
    secondary: { main: '#f59e0b' },
    background: { default: '#e0f2fe', paper: '#FFFFFF' },
    success: { main: '#10b981' },
    error: { main: '#ef4444' },
    warning: { main: '#f59e0b' },
    text: { primary: '#0f172a', secondary: '#475569' },
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

const accentColor = '#0284c7';
const sunshineColor = '#f59e0b';

// ─── Translations ───────────────────────────────────────────────────────────
const translations = {
  en: {
    found: 'Found',
    missing: 'Missing',
    sighting: 'Sighting',
    ownerFound: 'Owner Found',
    share: 'Share',
    description: 'Description',
    lastSeenHere: 'Last seen here',
    haveYouSeen: (name) => `Have you seen ${name}?`,
    didYouFind: (name) => `Did you find ${name}?`,
    downloadToNotify: 'Download PetGuardian to notify the owner',
    noAlarmId: 'No alarm ID provided.',
    alarmNotFound: 'Alarm not found.',
    loadFailed: 'Failed to load alarm.',
    downloadApp: 'Download the App',
    downloadDesc: 'Get PetGuardian free on Google Play to report sightings, create pet profiles, and broadcast alerts.',
    googlePlay: 'Google Play',
    scanQR: 'or scan the QR code',
  },
  es: {
    found: 'Encontrado',
    missing: 'Perdido',
    sighting: 'Avistamiento',
    ownerFound: 'Dueño Encontrado',
    share: 'Compartir',
    description: 'Descripción',
    lastSeenHere: 'Última vez visto aquí',
    haveYouSeen: (name) => `¿Has visto a ${name}?`,
    didYouFind: (name) => `¿Encontraste a ${name}?`,
    downloadToNotify: 'Descarga PetGuardian para notificar al dueño',
    noAlarmId: 'No se proporcionó un ID de alarma.',
    alarmNotFound: 'Alarma no encontrada.',
    loadFailed: 'Error al cargar la alarma.',
    downloadApp: 'Descarga la App',
    downloadDesc: 'Obtén PetGuardian gratis en Google Play para reportar avistamientos, crear perfiles de mascotas y emitir alertas.',
    googlePlay: 'Google Play',
    scanQR: 'o escanea el código QR',
  },
};

function getInitialLang() {
  const browserLang = (navigator.language || navigator.userLanguage || 'en').slice(0, 2).toLowerCase();
  return browserLang === 'es' ? 'es' : 'en';
}

// ─── Components ─────────────────────────────────────────────────────────────

function OSMMap({ latitude, longitude }) {
  const delta = 0.003;
  const bbox = `${longitude - delta},${latitude - delta},${longitude + delta},${latitude + delta}`;
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${latitude},${longitude}`;
  return (
    <iframe
      title="map"
      src={src}
      style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
      loading="lazy"
    />
  );
}

function DownloadCard({ t }) {
  const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.inclouder.petguardian';
  const qrUrl = 'https://api.qrserver.com/v1/create-qr-code/?size=160x160&color=080d16&data=https%3A%2F%2Fplay.google.com%2Fstore%2Fapps%2Fdetails%3Fid%3Dcom.inclouder.petguardian';

  return (
    <Box sx={{ px: 2, mt: 3, maxWidth: 700, mx: 'auto' }}>
      <Card elevation={0} sx={{
        borderRadius: 1,
        border: '1.5px solid #bae6fd',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}>
        <CardContent sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 3, py: 3 }}>
          {/* QR Code */}
          <Box sx={{
            flexShrink: 0,
            p: 1.5,
            bgcolor: '#f8fafc',
            borderRadius: 1,
            border: '1px solid #e2e8f0',
          }}>
            <Box
              component="img"
              src={qrUrl}
              alt="QR Code - Google Play"
              sx={{ width: 120, height: 120, display: 'block' }}
            />
            <Typography fontSize={10} color="text.secondary" fontWeight={600} textAlign="center" mt={1}>
              {t.scanQR}
            </Typography>
          </Box>

          {/* Text + button */}
          <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
            <Typography fontWeight={900} fontSize={17} color="text.primary" mb={0.5}>
              {t.downloadApp}
            </Typography>
            <Typography fontSize={13} color="text.secondary" fontWeight={500} lineHeight={1.6} mb={2}>
              {t.downloadDesc}
            </Typography>
            <Box
              component="a"
              href={playStoreUrl}
              target="_blank"
              rel="noopener"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 3,
                py: 1.5,
                bgcolor: '#0f172a',
                color: '#ffffff',
                borderRadius: '12px',
                fontWeight: 800,
                fontSize: 13,
                textDecoration: 'none',
                transition: 'background 0.2s',
                '&:hover': { bgcolor: '#1e293b' },
              }}
            >
              <Box
                component="img"
                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                alt="Google Play"
                sx={{ height: 20 }}
              />
              {t.googlePlay}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

function AlarmPage({ alarm, lang, toggleLang }) {
  const t = translations[lang];
  const [activeImage, setActiveImage] = useState(0);
  const images = alarm.images?.filter(Boolean) ?? [];
  const alarmType = alarm.type || 'missing';

  const statusColor = alarm.found
    ? '#10b981'
    : alarmType === 'warning'
      ? '#f59e0b'
      : '#ef4444';

  const statusLabel = alarmType === 'warning'
    ? (alarm.found ? t.ownerFound : t.sighting)
    : (alarm.found ? t.found : t.missing);

  const StatusIcon = alarm.found
    ? CheckCircleIcon
    : alarmType === 'warning'
      ? WarningIcon
      : ErrorIcon;

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: `${alarm.name} – PetGuardian`, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 4 }}>

      {/* Header */}
      <Box sx={{
        display: 'flex', flexDirection: 'row', alignItems: 'center',
        justifyContent: 'space-between',
        px: 3, py: 1.5,
        bgcolor: 'rgba(255,255,255,0.9)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #bae6fd',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src="/logo.png"
            alt="PetGuardian"
            sx={{ height: 48, objectFit: 'contain' }}
          />
          <Typography fontWeight={900} fontSize={18} color="text.primary">
            Pet Guardian
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Language toggle */}
          <Tooltip title={lang === 'es' ? 'Switch to English' : 'Cambiar a Español'}>
            <IconButton onClick={toggleLang} sx={{ color: 'text.secondary' }}>
              <LanguageIcon fontSize="small" />
              <Typography fontSize={11} fontWeight={800} ml={0.3}>
                {lang.toUpperCase()}
              </Typography>
            </IconButton>
          </Tooltip>
          <Box sx={{
            px: 2, py: 0.75,
            borderRadius: '12px',
            bgcolor: statusColor,
            color: '#ffffff',
            fontWeight: 800,
            fontSize: 12,
            letterSpacing: '0.05em',
          }}>
            {statusLabel.toUpperCase()}
          </Box>
          <Tooltip title={t.share}>
            <IconButton onClick={handleShare} sx={{ color: 'text.secondary' }}>
              <ShareIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main image */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 1 }}>
        <Box sx={{
          width: 200, height: 200, borderRadius: '50%',
          bgcolor: '#f0f9ff', p: '5px',
          boxShadow: `0 0 0 4px ${statusColor}, 0 8px 32px rgba(0,0,0,0.1)`,
        }}>
          <Avatar
            src={images[activeImage]}
            sx={{ width: '100%', height: '100%', fontSize: 64 }}
          >
            <PetsIcon sx={{ fontSize: 64 }} />
          </Avatar>
        </Box>
      </Box>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 2, mb: 1 }}>
          {images.map((img, i) => (
            <Avatar
              key={i}
              src={img}
              onClick={() => setActiveImage(i)}
              sx={{
                width: 56, height: 56, cursor: 'pointer',
                border: `${activeImage === i ? 3 : 1.5}px solid`,
                borderColor: activeImage === i ? statusColor : '#cbd5e1',
                opacity: activeImage === i ? 1 : 0.55,
                transition: 'all 0.2s',
              }}
            />
          ))}
        </Box>
      )}

      {/* Status chip */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
        <Chip
          icon={<StatusIcon sx={{ color: `${statusColor} !important` }} />}
          label={statusLabel}
          sx={{
            bgcolor: `${statusColor}18`,
            color: statusColor,
            fontWeight: 800,
            fontSize: 14,
            px: 1.5,
            border: `1.5px solid ${statusColor}44`,
            borderRadius: '12px',
          }}
        />
      </Box>

      {/* Pet name */}
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Typography fontWeight={900} fontSize={22} color="text.primary">
          {alarm.name}
        </Typography>
        {alarm.date && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
            <CalendarTodayIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
            <Typography fontSize={13} color="text.secondary" fontWeight={500}>
              {alarm.date}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Info cards */}
      <Box sx={{ px: 2, mt: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, maxWidth: 700, mx: 'auto' }}>

        {/* Description card */}
        <Card elevation={0} sx={{ flex: 1, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid #e2e8f0' }}>
          <CardContent>
            <Typography fontWeight={800} fontSize={13} mb={1} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
              {t.description}
            </Typography>
            <Typography fontSize={14} lineHeight={1.7} color="text.primary" fontWeight={500}>
              {alarm.description || '—'}
            </Typography>
          </CardContent>
        </Card>

        {/* Map card */}
        {alarm.latitude && alarm.longitude && (
          <Card elevation={0} sx={{ flex: 1, borderRadius: 1, overflow: 'hidden', minHeight: 160, border: '1px solid #e2e8f0' }}>
            <Box sx={{ position: 'relative', height: 160 }}>
              <OSMMap latitude={alarm.latitude} longitude={alarm.longitude} />
              <Box sx={{
                position: 'absolute', bottom: 8, left: 8,
                display: 'flex', alignItems: 'center', gap: 0.5,
                bgcolor: 'rgba(15,23,42,0.75)', borderRadius: 2, px: 1.5, py: 0.5,
              }}>
                <LocationOnIcon sx={{ fontSize: 14, color: statusColor }} />
                <Typography fontSize={11} color="white" fontWeight={600}>{t.lastSeenHere}</Typography>
              </Box>
            </Box>
          </Card>
        )}
      </Box>

      {/* Found pet CTA */}
      {!alarm.found && (
        <Box sx={{ px: 2, mt: 3, maxWidth: 700, mx: 'auto' }}>
          <Card elevation={0} sx={{
            borderRadius: 1, bgcolor: `${statusColor}12`,
            border: `1.5px solid ${statusColor}44`, cursor: 'default',
          }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'white', width: 52, height: 52, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                <PetsIcon sx={{ color: '#0f172a', fontSize: 28 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={800} fontSize={15} color="text.primary">
                  {alarmType === 'warning' ? t.haveYouSeen(alarm.name) : t.didYouFind(alarm.name)}
                </Typography>
                <Typography fontSize={12} color="text.secondary" mt={0.5} fontWeight={500}>
                  {t.downloadToNotify}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Download card */}
      <DownloadCard t={t} />

      {/* Footer */}
      <Box sx={{ textAlign: 'center', mt: 5, pt: 3, borderTop: '1px solid #bae6fd' }}>
        <Typography fontSize={12} color="text.secondary" fontWeight={600}>
          &copy; {new Date().getFullYear()} PetGuardian
        </Typography>
      </Box>
    </Box>
  );
}

export default function App() {
  const [alarm, setAlarm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lang, setLang] = useState(getInitialLang);

  const t = translations[lang];
  const toggleLang = () => setLang((prev) => (prev === 'es' ? 'en' : 'es'));

  const alarmId = new URLSearchParams(window.location.search).get('id');

  useEffect(() => {
    if (!alarmId) { setError('noAlarmId'); setLoading(false); return; }
    const storage = getStorage();
    getDoc(doc(db, 'alarms', alarmId))
      .then(async snap => {
        if (!snap.exists()) { setError('alarmNotFound'); return; }
        const data = { id: snap.id, ...snap.data() };
        const imagePromises = [1, 2, 3].map(i =>
          getDownloadURL(ref(storage, `alarms/${alarmId}/image_${i}.jpg`)).catch(() => null)
        );
        data.images = (await Promise.all(imagePromises)).filter(Boolean);
        setAlarm(data);
      })
      .catch(() => setError('loadFailed'))
      .finally(() => setLoading(false));
  }, [alarmId]);

  return (
    <ThemeProvider theme={lightTheme}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#e0f2fe' }}>
          <CircularProgress sx={{ color: accentColor }} />
        </Box>
      ) : error ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#e0f2fe', gap: 2 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ErrorIcon sx={{ color: '#ef4444', fontSize: 28 }} />
          </Box>
          <Typography color="error" fontWeight={700} fontSize={16}>{t[error]}</Typography>
        </Box>
      ) : (
        <AlarmPage alarm={alarm} lang={lang} toggleLang={toggleLang} />
      )}
    </ThemeProvider>
  );
}
